import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';
import RangePlanTable from '@components/rangePlan/RangePlanTable';
import { ENTITY_TYPE } from '@components/rangePlan/constants';

import CollectionPresentation from '@features/collection/components/CollectionPresentation';
import StyleDraftBoard from '@features/collection/details/StyleDraftBoard';
import AddNewFolder from '@features/favourites/components/AddNewFolder';
import FavouriteDetailsTopFilter from '@features/favourites/components/FavouriteDetailsTopFilter';
import FavouriteDetailsTopHeader from '@features/favourites/components/FavouriteDetailsTopHeader';
import ProductFavourite from '@features/favourites/components/ProductFavourite';
import SidePanel from '@features/favourites/components/SidePanel';
import { FAVOURITES_DETAILS_TABS } from '@features/favourites/utils/favouriteUtil';
import {
	allFilterPages,
	collectionDetailsFieldList,
	getFilterParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import FileUploads from '@features/blockSuggestion/components/fileUpload/FileUploads';

import { clearGlobalFilter, replaceDetailsPageFilterFields } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	fetchFavouriteProducts,
	fetchTotalLikeCount,
	setActiveFavouritePageTab
} from '@store/favourite/actions';
import { getFavouriteProducts } from '@store/reducers';

import { getProductDetailsFilterFields } from '@services/collectionService';
import { setSystemUserPreferences } from '@services/commonService';
import {
	getDraftedProducts,
	getFolderById,
	removeFromDraft,
	undraftProduct
} from '@services/favouriteService';

import { getUserPreference, printApiError } from '@utils/Util';

import { LIST_VIEW_TYPE } from '@/constants/constant';

import SampleComment from '@/components/SampleComment';
import CommonImage from '@/components/CommonImage';
import {
	useAddPersonalSettingMutation,
	useGetPersonalSettingsQuery,
	useArtboardProductTagMutation
} from '@/api/commonApis';
import BulkPresentationUploadModal from '@/features/favourites/components/BulkPresentationUploadModal';

const FavouriteDetails = () => {
	const userPreference = getUserPreference();
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [designList, setDesignList] = useState({
		data: [],
		totalPages: 0,
		totalElements: 0,
		currentPage: 0
	});
	const { id } = useParams();

	let allFavouritesForProducts = useSelector(getFavouriteProducts);
	const [defaultViewType, setDefaultViewType] = useState(
		userPreference?.FAVOURITE_VIEW_TYPE || 'LARGE_VIEW'
	);

	const [selectedProducts, setSelectedProducts] = useState([]);
	const [selectedSortingOption, setSelectedSortingOption] = useState(
		userPreference?.FAVOURITE_PRODUCT_SORTING_OPTION || { code: 'NEWEST', title: 'Newest first' }
	);
	const [isSearchBoxHidden, setIsSearchBoxHidden] = useState(true);
	const [isShowSidePanel, setIsShowSidePanel] = useState(false);
	const [isShowDraft, setIsShowDraft] = useState(false);
	const [isEditFolder, setIsEditFolder] = useState(false);
	const [folderInfo, setFolderInfo] = useState(null);
	const [selectedFolderToEdit, setSelectedFolderToEdit] = useState(null);
	const [showComment, setShowComment] = useState(false);
	const dispatch = useDispatch();
	const [isPlayPresentation, setIsPlayPresentation] = useState(false);
	const [draftedProducts, setDraftedProducts] = useState([]);
	const activeTab = useSelector((store) => store.favourite?.favouritePageTab);
	const filterFields = useSelector((store) => store?.filterFieldStore);
	const [showFilter, setShowFilter] = useState(false);
	const location = useLocation();
	const [currentScrolledProduct, setCurrentScrolledProduct] = useState([]);
	const [artboardProducts, setArtboardProducts] = useState(null);
	const [isShowFileUploadModal, setIsShowFileUploadModal] = useState(false);
	const [isUploadedPresentation, setIsUploadedPresentation] = useState(false);
	const [uploadedProductIds, setUploadedProductIds] = useState([]);

	const [addPersonalSetting] = useAddPersonalSettingMutation();
	const [artboardProductTag, { data: artboardProductTags }] = useArtboardProductTagMutation();

	const {
		data: personalSettings,
		isSuccess: personalSettingsSuccess,
		isError: personalSettingsError
	} = useGetPersonalSettingsQuery({
		settingKey: 'FOLDER_DETAILS_FILTER',
		params: { entityId: id, entityType: ENTITY_TYPE.FAVOURITE_FOLDER }
	});

	useEffect(() => {
		setArtboardProducts((prev) => ({
			...prev,
			...artboardProductTags
		}));
	}, [page, artboardProductTags]);

	useEffect(() => {
		if (
			personalSettings?.value &&
			parseInt(personalSettings?.entityId) === parseInt(id) &&
			personalSettings?.entityType === ENTITY_TYPE.FAVOURITE_FOLDER
		) {
			dispatch(replaceDetailsPageFilterFields(JSON.parse(personalSettings?.value)));
		}
	}, [personalSettings?.value]);

	const SIZE = 30;

	useEffect(() => {
		if (isFilterFromSidePanel()) return;
		if (personalSettingsSuccess || personalSettingsError || id) {
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					'',
					selectedSortingOption?.code,
					id,
					false,
					createFilterParams(),
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW,
					setCurrentScrolledProduct
				)
			);
			setPage(0);
		}
	}, [selectedSortingOption?.code, activeTab, defaultViewType, filterFields, id]);

	useEffect(() => {
		if (currentScrolledProduct?.length > 0) {
			artboardProductTag(currentScrolledProduct);
		}
	}, [currentScrolledProduct?.length]);

	useEffect(() => {
		if (showFilter) {
			const { filterParams } = getFilterParams(getFilterFieldFromStore(), id, 'folderId', true);
			dispatch(getProductDetailsFilterFields({ ...filterParams }));
			if (Object.keys(getFilterFieldFromStore()).length > 0)
				addPersonalSetting({
					body: {
						key: 'FOLDER_DETAILS_FILTER',
						entityId: id,
						entityType: ENTITY_TYPE.FAVOURITE_FOLDER,
						value: JSON.stringify(filterFields)
					}
				});
		}
	}, [filterFields, showFilter]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const isShowComment = params.get('showComment');
		if (isShowComment) {
			setShowComment(true);
		}
	}, [id]);

	useEffect(() => {
		// Clear global filter when component unmounts
		return () => {
			dispatch(clearGlobalFilter());
		};
	}, []);

	const getFilterFieldFromStore = () => {
		return parseInt(id) === -1 ? filterFields : filterFields?.DETAILS_PAGE_FILTERS;
	};

	const createFilterParams = () => {
		const { body } = getFilterParams(getFilterFieldFromStore(), id, '', true);
		return body;
	};

	useEffect(() => {
		if (designList?.data?.length) {
			dispatch(fetchTotalLikeCount());
		}
	}, [designList?.data?.length]);

	const isFilterFromSidePanel = () => {
		const referenceNumber = filterFields?.DETAILS_PAGE_FILTERS?.referenceNumber;
		if (!referenceNumber?.length) return false;
		return referenceNumber[0]?.isAsyncSearch === true;
	};

	const fetchDraftedProducts = async () => {
		try {
			setIsLoading(true);
			const response = await getDraftedProducts({
				page: 0,
				size: 1000,
				sortingOption: selectedSortingOption?.code,
				search: '',
				folderId: id,
				isDrafted: true,
				isListView: false
			});

			const draftedData =
				response?.data?.map((item) => ({
					...item?.productResponse
				})) || [];

			setDraftedProducts(draftedData);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchDraftedProducts();
	}, [isShowDraft]);

	const handleDefaultViewType = async (viewType) => {
		const body = {
			...userPreference,
			FAVOURITE_VIEW_TYPE: viewType
		};
		setDefaultViewType(viewType);
		await setSystemUserPreferences(body);
	};

	const onChangeAllCheckAndSearchForStyle = async (e) => {
		let { name, value, checked } = e.target;
		if (name === 'allCheckBoxForStyle') {
			setSelectedProducts(checked ? designList?.data?.map((item) => item.id) : []);
		} else if (name === 'searchForStyle') {
			setIsLoading(true);
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					value,
					selectedSortingOption?.code,
					id,
					false,
					createFilterParams(),
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			).finally(() => {
				setIsLoading(false);
			});
		}
	};

	useEffect(() => {
		if (id) {
			fetchFolderById();
		}
	}, [id]);

	const fetchFolderById = async () => {
		try {
			setIsLoading(true);
			const response = await getFolderById(`?folderId=${id}`);
			setFolderInfo(response);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleEditFolder = (folder) => {
		setIsEditFolder(true);
		setSelectedFolderToEdit(folder);
	};

	const handleCloseModal = () => {
		setIsShowSidePanel(false);
		setIsEditFolder(false);
		setSelectedFolderToEdit(null);
	};

	const handleUndraftProduct = async (design) => {
		try {
			dispatch(openLoader());
			await removeFromDraft(design?.id, id);
			fetchDraftedProducts();
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					'',
					selectedSortingOption?.code,
					id,
					false,
					[],
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleUnLikeProduct = async (productId) => {
		try {
			dispatch(openLoader());
			await undraftProduct(productId, id);
			fetchDraftedProducts();
			dispatch(fetchTotalLikeCount());
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}

		setDraftedProducts(draftedProducts.filter((item) => parseInt(item?.id) !== parseInt(productId)));
	};

	const handleSortingOptionChange = async (option) => {
		setSelectedSortingOption(option);
		const body = {
			...userPreference,
			FAVOURITE_PRODUCT_SORTING_OPTION: option
		};
		await setSystemUserPreferences(body);
	};

	const isSoldFolder = () => folderInfo?.name === 'Sold Interest';
	const filteredTabs = isSoldFolder()
		? FAVOURITES_DETAILS_TABS.filter((tab) => tab.code !== 'RANGE_PLAN' && tab.code !== 'FILES')
		: FAVOURITES_DETAILS_TABS;

	return (
		<>
			<FavouriteDetailsTopHeader onEditFolder={handleEditFolder} folderInfo={folderInfo} folderId={id}>
				<div className="navigation-tab shadow-nav-4dp bg-white">
					<ul>
						{filteredTabs.map((item, index) => (
							<li
								key={`${item.code}-${index}`}
								className={`${activeTab.code === item.code ? 'active' : ''}`}
								onClick={() => dispatch(setActiveFavouritePageTab(item))}
							>
								{item.title}
							</li>
						))}
					</ul>
				</div>
				<div>
					{activeTab.code !== 'RANGE_PLAN' && activeTab.code !== 'FILES' && (
						<>
							<FavouriteDetailsTopFilter
								allFavouritesForProducts={allFavouritesForProducts}
								isSearchBoxHidden={isSearchBoxHidden}
								setIsSearchBoxHidden={setIsSearchBoxHidden}
								onChangeAllCheckAndSearchForStyle={onChangeAllCheckAndSearchForStyle}
								defaultViewType={defaultViewType}
								handleDefaultViewType={handleDefaultViewType}
								setIsPlayPresentation={setIsPlayPresentation}
								onSelectOption={handleSortingOptionChange}
								setPage={setPage}
								selectedSortingOption={selectedSortingOption}
								setIsShowSidePanel={setIsShowSidePanel}
								setIsShowDraft={setIsShowDraft}
								draftedProducts={draftedProducts}
								id={id}
								designList={designList}
								getCategoryIdList={createFilterParams}
								setShowFilter={setShowFilter}
								setIsShowFileUploadModal={setIsShowFileUploadModal}
								folderInfo={folderInfo}
								handleSuccess={fetchFolderById}
								isSoldFolder={isSoldFolder()}
							/>
						</>
					)}
				</div>
				{isShowSidePanel && (
					<SidePanel
						setIsShowSidePanel={setIsShowSidePanel}
						selectedSortingOption={selectedSortingOption}
						fetchFavouriteProducts={fetchFavouriteProducts}
						viewType={defaultViewType}
						setProductList={setDesignList}
						productList={designList}
						handleSortingOptionChange={handleSortingOptionChange}
					/>
				)}
				{isShowFileUploadModal && (
					<BulkPresentationUploadModal
						onClose={() => {
							setIsShowFileUploadModal(false);
							setIsUploadedPresentation(false);
							setUploadedProductIds([]);
						}}
						setIsUploadedPresentation={setIsUploadedPresentation}
						setUploadedProductIds={setUploadedProductIds}
						folderId={id}
					/>
				)}
			</FavouriteDetailsTopHeader>
			<section
				className={`${activeTab.code === 'FILES' ? 'bg-gray' : 'bg-white'} filter-data-container filter-v3-data-container py-0`}
			>
				{activeTab.code === 'DESIGN' && (
					<FilterComponent
						filterPageName={allFilterPages.FAVOURITE_DETAILS}
						fields={collectionDetailsFieldList}
						showFilter={showFilter}
						isDependentFilter={true}
						className="favourite-details-filter-bar"
					/>
				)}
				{activeTab.code === 'DESIGN' && (
					<div className="data-table">
						{folderInfo?.description !== undefined && (
							<div className="description-container mb-2">
								<p className="regular-14 gray_dark_02">{folderInfo?.description}</p>
							</div>
						)}
						<ProductFavourite
							isLoading={isLoading}
							setIsLoading={setIsLoading}
							designList={designList}
							setDesignList={setDesignList}
							selectedProducts={selectedProducts}
							setSelectedProducts={setSelectedProducts}
							defaultViewType={defaultViewType}
							allFavourites={allFavouritesForProducts}
							onChangeAllCheck={onChangeAllCheckAndSearchForStyle}
							selectedSortingOption={selectedSortingOption}
							page={page}
							setPage={setPage}
							folderId={id}
							folderInfo={folderInfo}
							fetchDraftedProducts={fetchDraftedProducts}
							getCategoryIdList={createFilterParams}
							brandId={folderInfo?.brandResponse?.id}
							createFilterParams={createFilterParams}
							artboardProducts={artboardProducts}
							setCurrentScrolledProduct={setCurrentScrolledProduct}
						/>
					</div>
				)}
				{activeTab.code === 'RANGE_PLAN' && (
					<div className="data-table mw-100">
						<RangePlanTable
							entityType={ENTITY_TYPE.FAVOURITE_FOLDER}
							brandName={folderInfo?.brandResponse?.name}
							brandId={folderInfo?.brandResponse?.id}
						/>
					</div>
				)}
				{activeTab.code === 'FILES' && (
					<div className="collection-file-section bg-white mx-auto p-5 mt-4">
						<FileUploads
							id={id}
							originFrom="FILE"
							onSetActiveTool={() => false}
							onPlaceImageOnArtboard={() => false}
							entityType="FAVOURITE_FOLDER"
							allowedFileType="*"
						/>
					</div>
				)}

				{id > 0 && (
					<div className="conversation-btn">
						<button
							className="button text absolute-conversation-btn"
							onClick={() => {
								setShowComment(true);
								setFolderInfo((prev) => ({
									...prev,
									noOfUnreadMessage: 0
								}));
							}}
						>
							<CommonImage src={`/icons/comments-gray.svg`} alt="not read" />
						</button>
					</div>
				)}
			</section>
			<div className="collection-details-container">
				{isShowDraft && (
					<StyleDraftBoard
						onClose={() => setIsShowDraft(false)}
						totalDraft={draftedProducts?.length}
						unDraftProduct={handleUndraftProduct}
						products={draftedProducts}
						parentComponent="FAVOURITE_DETAILS"
						onUnLike={handleUnLikeProduct}
					/>
				)}
			</div>

			{isEditFolder && (
				<ModalContainer className="sample-location-modal">
					<AddNewFolder
						onCloseModal={() => handleCloseModal()}
						isEdit={isEditFolder}
						selectedFolderToEdit={selectedFolderToEdit}
						handleSuccess={fetchFolderById}
					/>
				</ModalContainer>
			)}

			<CollectionPresentation
				isPlay={isPlayPresentation || isUploadedPresentation}
				collectionName={folderInfo?.name}
				collectionId={id}
				onCLoseModal={() => {
					setIsPlayPresentation(false);
					setIsUploadedPresentation(false);
				}}
				entityType="FAVOURITE_FOLDER"
				selectedProductIds={isUploadedPresentation ? uploadedProductIds : selectedProducts}
				isShowPSD={isUploadedPresentation}
			/>

			<SampleComment
				sampleRequestId={id}
				showComment={showComment}
				setShowComment={() => setShowComment(false)}
				brandInfo={{
					brandId: folderInfo?.brandResponse?.id,
					brandName: folderInfo?.brandResponse?.name
				}}
				postTypeFilter={'FAVOURITE_FOLDER_COMMENT'}
				getPostUrl={'getFavouriteConversation'}
				addPostUrl={'addNewConversationPost'}
				postTypeId={'favouriteFolderId'}
				topic={'FOLDER_CONVERSATION'}
				styleList={designList?.data}
			/>
		</>
	);
};

export default FavouriteDetails;
